// src/utils/deleteError.ts
/**
 * Turns a failed delete into something an admin can act on.
 *
 * The four queue deletes answer with meaningful HTTP statuses rather than a single "failed", and each
 * one calls for a different response from the person at the screen. Passing the raw message through
 * would tell them a request failed without telling them whether to retry, ask for a setting to be
 * turned on, or stop trying altogether.
 *
 * | Status | Means | What the admin should do |
 * | --- | --- | --- |
 * | 400 | approved, with a registration or case behind it (**intakes and requests only**) | nothing — it cannot be deleted |
 * | 403 | the module's delete setting is off for this action | ask for it to be switched on |
 * | 404 | already gone | refresh |
 *
 * ⚠️ A **403 means the screen and the server disagree**, since the UI hides the action when the setting
 * is off. The most likely cause is a settings change since the page loaded, so the message says to
 * reload rather than implying the admin did something wrong.
 */

export type DeleteFailureKind = 'approved' | 'forbidden' | 'notFound' | 'unknown';

export interface DeleteFailure {
  kind: DeleteFailureKind;
  status?: number;
  /** Toast heading. */
  title: string;
  /** Toast body, in plain words. */
  message: string;
  /** False when retrying cannot possibly help, so the caller can stop offering it. */
  retryable: boolean;
}

/** `CCMSError` carries the HTTP status; anything else is treated as unknown. */
function statusOf(e: unknown): number | undefined {
  const s = (e as { status?: unknown })?.status;
  return typeof s === 'number' ? s : undefined;
}

const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e ?? '')).trim();

/**
 * @param entity singular, lower case — "intake", "request", "case", "registration".
 * @param entities plural for the same word.
 */
export function classifyDeleteError(
  e: unknown,
  entity: string,
  entities = `${entity}s`,
): DeleteFailure {
  const status = statusOf(e);
  const server = messageOf(e);

  if (status === 400) {
    return {
      kind: 'approved',
      status,
      title: 'Already approved',
      // ⚠️ The server's own message names what was created, so it is preferred over a guess.
      message: server
        || `This ${entity} has already been approved and has a record behind it, so it can no longer be `
          + 'deleted. Cancel it instead.',
      retryable: false,
    };
  }

  if (status === 403) {
    return {
      kind: 'forbidden',
      status,
      title: 'Not permitted',
      message: `Deleting ${entities} is switched off for this module. This screen may be out of date — `
        + 'reload it, and check the Deletion settings under Administration.',
      retryable: false,
    };
  }

  if (status === 404) {
    return {
      kind: 'notFound',
      status,
      title: 'Already gone',
      message: `That ${entity} no longer exists. Refresh the list to see the current queue.`,
      retryable: false,
    };
  }

  return {
    kind: 'unknown',
    status,
    title: 'Could not delete',
    message: server || `The ${entity} could not be deleted. Please try again.`,
    retryable: true,
  };
}
